import { useState, useEffect } from "react";
import { type Article, type Category } from "@/data/articles";
import { fetchDBArticles, fetchDBArticlesListing, type DBArticle } from "@/lib/article-db";
import { optimizeImageUrl } from "@/lib/image-utils";

function sortByDate(arr: DBArticle[]): DBArticle[] {
  return [...arr].sort((a, b) => new Date(b.publicationDate).getTime() - new Date(a.publicationDate).getTime());
}

let listingPromiseCache: Promise<DBArticle[]> | null = null;

function getListingPromise(): Promise<DBArticle[]> {
  if (!listingPromiseCache) {
    listingPromiseCache = fetchDBArticlesListing();
  }
  return listingPromiseCache;
}

function preloadHeroImage(articles: DBArticle[]) {
  const hero = sortByDate(articles.filter((a) => a.isHero))[0]
    ?? sortByDate(articles.filter((a) => a.isTrending))[0]
    ?? sortByDate(articles.filter((a) => a.isFeature))[0];

  if (hero?.imageUrl && typeof document !== "undefined") {
    const optimizedUrl = optimizeImageUrl(hero.imageUrl, { width: 900, quality: 75 });
    const existing = document.head.querySelector(`link[rel="preload"][href="${optimizedUrl}"]`);

    if (!existing) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = optimizedUrl;
      document.head.appendChild(link);
    }
  }
}

export function useArticles(lightweight = false) {
  const [allArticles, setAllArticles] = useState<DBArticle[]>([]);
  const [loading, setLoading] = useState(!lightweight);

  useEffect(() => {
    let cancelled = false;

    if (lightweight) {
      getListingPromise()
        .then((dbArticles) => {
          if (cancelled) return;
          const sortedArticles = sortByDate(dbArticles);
          preloadHeroImage(sortedArticles);
          setAllArticles(sortedArticles);
        })
        .catch(() => {
          if (!cancelled) setAllArticles([]);
        });

      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    fetchDBArticles()
      .then((dbArticles) => {
        if (!cancelled) {
          setAllArticles(sortByDate(dbArticles));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAllArticles([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lightweight]);

  const getBySlug = (slug: string) => allArticles.find((a) => a.slug === slug);
  const getByCategory = (category: Category) => allArticles.filter((a) => a.category === category);
  const getRelated = (article: Article, count = 3) =>
    allArticles
      .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
      .slice(0, count);

  const heroArticles = allArticles
    .filter((a) => a.isHero)
    .sort((a, b) => (a.heroPosition ?? 99) - (b.heroPosition ?? 99))
    .slice(0, 3);
  const latestArticles = sortByDate(allArticles.filter((a) => a.isLatest));
  const trendingArticles = sortByDate(allArticles.filter((a) => a.isTrending));

  return { articles: allArticles, loading, getBySlug, getByCategory, getRelated, heroArticles, latestArticles, trendingArticles };
}
